// src/stations/comprehension/contextLens.ts — KS2 Vocabulary & Nuance Station (Domain 2a/2g)
// Implements FR-ENG-004, NFR-TOUCH-001, NFR-STYLE-001, CON-003.
// Tudor explores tricky marine vocabulary words with a tactile definition drawer and 2-question mastery checks.

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';
import { ComprehensionStorage, type VocabAttempt } from '@storage/comprehensionStorage';
import type { ClaweyHud } from '@ui/components/claweyHud';
import rawVocabulary from '@content/vocabulary.json';

export interface VocabQuestion {
  id: string;
  prompt: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface VocabularyWord {
  id: string;
  word: string;
  phonetic: string;
  partOfSpeech: string;
  definition: string;
  marineExample: string;
  difficulty: 'easy' | 'medium' | 'hard';
  synonyms: string[];
  antonyms: string[];
  questions: VocabQuestion[];
}

export class ContextLensStation {
  private container: HTMLElement;
  private hud?: ClaweyHud;
  private vocabulary: VocabularyWord[];
  private currentIndex: number = 0;
  private isDrawerOpen: boolean = false;
  private activeQuestionIndex: number = 0;
  private selectedOptionIndex: number | null = null;
  private hasAnsweredCurrentQuestion: boolean = false;
  private isCurrentAnswerCorrect: boolean = false;
  private sessionCorrectCount: number = 0;

  constructor(hud?: ClaweyHud, initialWordIndex: number = 0) {
    this.container = document.createElement('section');
    this.container.id = 'context-lens-station';
    this.container.className = 'station-container';
    this.hud = hud;
    this.vocabulary = rawVocabulary as VocabularyWord[];
    this.currentIndex = Math.max(0, Math.min(initialWordIndex, this.vocabulary.length - 1));
  }

  setHud(hud: ClaweyHud): void {
    this.hud = hud;
  }

  render(): HTMLElement {
    this.updateView();
    return this.container;
  }

  private updateView(): void {
    const currentWord = this.vocabulary[this.currentIndex]!;
    const saved = ComprehensionStorage.load();
    const isMastered = saved.masteredVocabWords.includes(currentWord.word.toLowerCase());
    const isMuted = soundEngine.isMuted();

    this.container.innerHTML = `
      <!-- Header -->
      <div class="morris-card morris-card-gold" style="margin-bottom: 20px; padding: 24px 20px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px;">
          <div style="flex: 1; min-width: 260px;">
            <div style="display: inline-flex; align-items: center; gap: 8px; padding: 4px 12px; border: 1px solid ${tokens.color.gildedBrass}; border-radius: 20px; font-size: 0.8rem; color: ${tokens.color.madderCoral}; font-weight: 800; text-transform: uppercase; margin-bottom: 10px; background: rgba(255, 246, 229, 0.7);">
              <span>🔎</span> KS2 Reading Domain 2a & 2g · Vocabulary & Nuance
            </div>
            <h1 style="font-size: 2.1rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 8px;">
              Context Lens: Marine Lexicon
            </h1>
            <p style="font-size: 1rem; color: #3A4D66; max-width: 600px; line-height: 1.6; margin: 0;">
              Tap flagged words to expand the <strong>Naturalist Definition Drawer</strong>. Then complete the 2-question mastery check to prove your understanding!
            </p>
          </div>

          <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
            <!-- Vocabulary Streak -->
            <div style="text-align: center; padding: 10px 16px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px;">
              <div style="font-size: 0.7rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Vocab Streak</div>
              <div style="font-family: ${tokens.font.mono}; font-size: 1.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; line-height: 1.1;">
                🔥 ${saved.streakCount}
              </div>
            </div>

            <!-- Mastered Count -->
            <div style="text-align: center; padding: 10px 16px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.seaweedSage}; border-radius: 12px;">
              <div style="font-size: 0.7rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase;">Mastered</div>
              <div style="font-family: ${tokens.font.mono}; font-size: 1.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; line-height: 1.1;">
                ${saved.masteredVocabWords.length} / ${this.vocabulary.length}
              </div>
            </div>

            <!-- Sound Toggle -->
            <button id="btn-vocab-sound-toggle" class="touch-target" style="padding: 8px 14px; border-radius: 20px; font-size: 0.85rem; font-weight: 800; background: rgba(26, 54, 93, 0.08); border: 1px solid rgba(26, 54, 93, 0.2); color: ${tokens.color.morrisIndigo}; cursor: pointer;">
              ${isMuted ? '🔇 Off' : '🔊 Sound'}
            </button>
          </div>
        </div>
      </div>

      <!-- Main Stage: Word Spotlight & Observation Card -->
      <section class="morris-card" style="padding: 32px 28px; margin-bottom: 24px; position: relative;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 20px;">
          <div>
            <div style="display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap;">
              <h2 style="font-size: 2.4rem; color: ${tokens.color.morrisIndigo}; margin: 0;">
                ${currentWord.word}
              </h2>
              <span style="font-family: ${tokens.font.mono}; color: ${tokens.color.seaweedSage}; font-size: 1.1rem; font-weight: 600;">
                ${currentWord.phonetic}
              </span>
              <span style="font-style: italic; color: #5A6D86; font-size: 1rem;">
                (${currentWord.partOfSpeech})
              </span>
              ${isMastered ? '<span style="font-size: 0.8rem; padding: 2px 10px; border-radius: 12px; background: rgba(61, 90, 69, 0.15); color: #3D5A45; font-weight: 800;">✓ Mastered</span>' : ''}
            </div>
          </div>

          <div style="display: flex; gap: 10px; align-items: center;">
            <button id="btn-prev-word" class="touch-target" style="padding: 8px 16px; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 20px;" ${this.currentIndex === 0 ? 'disabled style="opacity: 0.4;"' : ''}>
              &larr; Prev Word
            </button>
            <span style="font-size: 0.9rem; font-weight: 700; color: ${tokens.color.morrisIndigo};">
              Word ${this.currentIndex + 1} of ${this.vocabulary.length}
            </span>
            <button id="btn-next-word" class="touch-target" style="padding: 8px 16px; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 20px;" ${this.currentIndex === this.vocabulary.length - 1 ? 'disabled style="opacity: 0.4;"' : ''}>
              Next Word &rarr;
            </button>
          </div>
        </div>

        <!-- Field Observation Card with Flagged Word Interaction -->
        <div style="padding: 24px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid rgba(229, 169, 60, 0.5); border-radius: 14px; margin-bottom: 24px; line-height: 1.7; font-size: 1.2rem; color: #1A365D;">
          <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; margin-bottom: 8px;">
            📖 Marine Field Observation
          </div>
          <p style="margin: 0; font-size: 1.25rem;">
            ${this.renderObservationTextWithHighlight(currentWord.marineExample, currentWord.word)}
          </p>
          <div style="margin-top: 12px; font-size: 0.9rem; color: #5A6D86;">
            💡 <em>Tap the highlighted word above or the button below to inspect Clawey's field definition drawer.</em>
          </div>
        </div>

        <!-- Definition Drawer Toggle Button -->
        <div style="margin-bottom: 24px;">
          <button id="btn-toggle-drawer" class="touch-target ${this.isDrawerOpen ? 'btn-gold' : 'btn-primary'}" style="padding: 12px 24px; font-size: 1.05rem; display: inline-flex; align-items: center; gap: 8px;">
            <span>${this.isDrawerOpen ? '📖 Close Definition Drawer' : '🔍 Open Definition Drawer & Clues'}</span>
          </button>
        </div>

        <!-- Tactile Naturalist Definition Drawer -->
        ${this.renderDefinitionDrawer(currentWord)}

        <!-- 2-Question Mastery Check Station -->
        ${this.renderMasteryCheckSection(currentWord)}
      </section>

      <!-- Bottom Station Navigation Link -->
      <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-top: 20px;">
        <a href="#/read" class="touch-target" style="padding: 10px 20px; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 12px; text-decoration: none; color: ${tokens.color.morrisIndigo}; font-weight: 700;">
          🔍 Switch to Clue Hunter (Domain 2d)
        </a>
        <a href="#/fact-diver" class="touch-target" style="padding: 10px 20px; border: 1.5px solid ${tokens.color.seaweedSage}; background: #FFFDF9; border-radius: 12px; text-decoration: none; color: ${tokens.color.seaweedSage}; font-weight: 700;">
          🤿 Switch to Fact Diver (Domain 2b) &rarr;
        </a>
      </div>
    `;

    this.attachEventListeners();
  }

  private renderObservationTextWithHighlight(sentence: string, targetWord: string): string {
    const regex = new RegExp(`\\b(${targetWord})\\b`, 'i');
    return sentence.replace(
      regex,
      `<span id="highlighted-target-word" class="touch-target" style="background: rgba(0, 229, 255, 0.25); border-bottom: 2.5px solid ${tokens.color.madderCoral}; padding: 2px 6px; border-radius: 4px; font-weight: 800; cursor: pointer; color: ${tokens.color.morrisIndigo}; display: inline-block;">$1</span>`
    );
  }

  private renderDefinitionDrawer(word: VocabularyWord): string {
    if (!this.isDrawerOpen) return '';

    return `
      <div id="vocab-definition-drawer" style="padding: 24px; background: rgba(250, 246, 237, 0.98); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 14px; margin-bottom: 28px; box-shadow: 0 8px 24px rgba(10, 25, 47, 0.12); animation: fadeIn 0.25s ease;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <span style="font-size: 0.85rem; color: ${tokens.color.madderCoral}; font-weight: 800; text-transform: uppercase;">
            📜 Naturalist Field Lexicon
          </span>
          <span style="font-size: 0.85rem; color: #5A6D86;">
            Difficulty: <strong>${word.difficulty.toUpperCase()}</strong>
          </span>
        </div>

        <p style="font-size: 1.25rem; font-family: ${tokens.font.body}; line-height: 1.6; color: ${tokens.color.morrisIndigo}; margin: 0 0 18px;">
          <strong>Definition:</strong> ${word.definition}
        </p>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px;">
          <div style="padding: 14px; background: rgba(255, 253, 249, 0.9); border-radius: 10px; border: 1px solid rgba(229, 169, 60, 0.3);">
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; margin-bottom: 6px;">
              🟢 Close Synonyms
            </div>
            <div style="display: flex; gap: 6px; flex-wrap: wrap;">
              ${word.synonyms.map((s) => `<span style="font-size: 0.9rem; padding: 4px 10px; background: rgba(61, 90, 69, 0.1); border-radius: 6px; font-weight: 700;">${s}</span>`).join('')}
            </div>
          </div>

          <div style="padding: 14px; background: rgba(255, 253, 249, 0.9); border-radius: 10px; border: 1px solid rgba(229, 169, 60, 0.3);">
            <div style="font-size: 0.8rem; color: ${tokens.color.madderCoral}; font-weight: 800; text-transform: uppercase; margin-bottom: 6px;">
              🔴 Opposites (Antonyms)
            </div>
            <div style="display: flex; gap: 6px; flex-wrap: wrap;">
              ${word.antonyms.map((a) => `<span style="font-size: 0.9rem; padding: 4px 10px; background: rgba(255, 107, 74, 0.1); border-radius: 6px; font-weight: 700;">${a}</span>`).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderMasteryCheckSection(word: VocabularyWord): string {
    const qIndex = this.activeQuestionIndex;
    const currentQ = word.questions[qIndex];
    if (!currentQ) return '';

    return `
      <div style="padding: 24px; background: rgba(255, 253, 249, 0.98); border: 1.5px solid ${tokens.color.morrisIndigo}; border-radius: 14px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px;">
          <span style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; text-transform: uppercase;">
            🎯 Mastery Check: Question ${qIndex + 1} of ${word.questions.length}
          </span>
          <span style="font-size: 0.8rem; padding: 2px 8px; border-radius: 8px; background: rgba(0, 229, 255, 0.15); font-weight: 700; color: ${tokens.color.morrisIndigo};">
            2 Questions per session (FR-ENG-004)
          </span>
        </div>

        <h3 style="font-size: 1.25rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 18px; line-height: 1.5;">
          ${currentQ.prompt}
        </h3>

        <!-- Options Grid -->
        <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px;">
          ${currentQ.options
            .map((opt, idx) => {
              const isSelected = this.selectedOptionIndex === idx;
              let bg = '#FFFDF9';
              let borderColor = 'rgba(26, 54, 93, 0.2)';
              let icon = '🔘';

              if (this.hasAnsweredCurrentQuestion) {
                if (idx === currentQ.correctIndex) {
                  bg = 'rgba(61, 90, 69, 0.15)';
                  borderColor = tokens.color.seaweedSage;
                  icon = '✅';
                } else if (isSelected) {
                  bg = 'rgba(255, 107, 74, 0.15)';
                  borderColor = tokens.color.madderCoral;
                  icon = '❌';
                }
              } else if (isSelected) {
                bg = 'rgba(229, 169, 60, 0.15)';
                borderColor = tokens.color.gildedBrass;
                icon = '🔵';
              }

              return `
                <button
                  class="touch-target btn-vocab-option"
                  data-index="${idx}"
                  style="display: flex; align-items: center; gap: 12px; text-align: left; padding: 14px 18px; font-size: 1.05rem; border: 2px solid ${borderColor}; background: ${bg}; border-radius: 12px; cursor: pointer; transition: all 0.2s ease;"
                  ${this.hasAnsweredCurrentQuestion ? 'disabled' : ''}
                >
                  <span style="font-size: 1.2rem;">${icon}</span>
                  <span style="flex: 1; color: ${tokens.color.morrisIndigo}; font-weight: 600;">${opt}</span>
                </button>
              `;
            })
            .join('')}
        </div>

        <!-- Action / Submit / Next buttons -->
        ${this.renderMasteryActionBar(word, currentQ)}
      </div>
    `;
  }

  private renderMasteryActionBar(word: VocabularyWord, currentQ: VocabQuestion): string {
    if (!this.hasAnsweredCurrentQuestion) {
      const canSubmit = this.selectedOptionIndex !== null;
      return `
        <button
          id="btn-vocab-submit"
          class="touch-target btn-primary"
          style="padding: 14px 28px; font-size: 1.1rem; width: 100%; max-width: 320px;"
          ${canSubmit ? '' : 'disabled style="opacity: 0.5;"'}
        >
          Check Meaning &rarr;
        </button>
      `;
    }

    const isLastQuestion = this.activeQuestionIndex >= word.questions.length - 1;

    return `
      <div style="padding: 16px; background: rgba(250, 246, 237, 0.9); border-radius: 10px; border-left: 4px solid ${this.isCurrentAnswerCorrect ? tokens.color.seaweedSage : tokens.color.madderCoral}; margin-bottom: 16px;">
        <div style="font-weight: 800; font-size: 1.1rem; color: ${this.isCurrentAnswerCorrect ? tokens.color.seaweedSage : tokens.color.madderCoral}; margin-bottom: 6px;">
          ${this.isCurrentAnswerCorrect ? '🌟 Brilliant deduction, Tudor!' : '🌊 Field Note from Clawey:'}
        </div>
        <p style="margin: 0; font-size: 1.05rem; line-height: 1.5; color: ${tokens.color.morrisIndigo};">
          ${currentQ.explanation}
        </p>
      </div>

      <div style="display: flex; gap: 14px; flex-wrap: wrap;">
        ${!isLastQuestion ? `
          <button id="btn-vocab-next-q" class="touch-target btn-primary" style="padding: 12px 24px; font-size: 1.05rem;">
            Next Question (2 of 2) &rarr;
          </button>
        ` : `
          <button id="btn-vocab-finish-word" class="touch-target btn-primary" style="padding: 12px 24px; font-size: 1.05rem;">
            ${this.currentIndex < this.vocabulary.length - 1 ? 'Explore Next Word &rarr;' : 'Restart Lexicon Explorer 🔄'}
          </button>
        `}
      </div>
    `;
  }

  private attachEventListeners(): void {
    // Sound toggle
    const soundToggle = this.container.querySelector<HTMLButtonElement>('#btn-vocab-sound-toggle');
    soundToggle?.addEventListener('click', () => {
      soundEngine.toggleMute();
      this.updateView();
    });

    // Drawer toggle via button or tapped highlighted word
    const drawerBtn = this.container.querySelector<HTMLButtonElement>('#btn-toggle-drawer');
    drawerBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.isDrawerOpen = !this.isDrawerOpen;
      this.updateView();
    });

    const highlightedWord = this.container.querySelector<HTMLElement>('#highlighted-target-word');
    highlightedWord?.addEventListener('click', () => {
      soundEngine.playTap();
      this.isDrawerOpen = true;
      this.updateView();
    });

    // Word navigation
    const prevBtn = this.container.querySelector<HTMLButtonElement>('#btn-prev-word');
    prevBtn?.addEventListener('click', () => {
      if (this.currentIndex > 0) {
        soundEngine.playTap();
        this.changeWord(this.currentIndex - 1);
      }
    });

    const nextBtn = this.container.querySelector<HTMLButtonElement>('#btn-next-word');
    nextBtn?.addEventListener('click', () => {
      if (this.currentIndex < this.vocabulary.length - 1) {
        soundEngine.playTap();
        this.changeWord(this.currentIndex + 1);
      }
    });

    // Option selection
    const optionButtons = this.container.querySelectorAll<HTMLButtonElement>('.btn-vocab-option');
    optionButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.hasAnsweredCurrentQuestion) return;
        soundEngine.playTap();
        this.selectedOptionIndex = parseInt(btn.getAttribute('data-index') ?? '0', 10);
        this.updateView();
      });
    });

    // Submit answer
    const submitBtn = this.container.querySelector<HTMLButtonElement>('#btn-vocab-submit');
    submitBtn?.addEventListener('click', () => {
      if (this.selectedOptionIndex === null) return;
      this.processAnswer();
    });

    // Next question within session
    const nextQBtn = this.container.querySelector<HTMLButtonElement>('#btn-vocab-next-q');
    nextQBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.activeQuestionIndex += 1;
      this.selectedOptionIndex = null;
      this.hasAnsweredCurrentQuestion = false;
      this.isCurrentAnswerCorrect = false;
      this.updateView();
    });

    // Finish current word / explore next word
    const finishWordBtn = this.container.querySelector<HTMLButtonElement>('#btn-vocab-finish-word');
    finishWordBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      const nextIdx = (this.currentIndex + 1) % this.vocabulary.length;
      this.changeWord(nextIdx);
    });
  }

  private processAnswer(): void {
    const currentWord = this.vocabulary[this.currentIndex]!;
    const currentQ = currentWord.questions[this.activeQuestionIndex]!;
    const isCorrect = this.selectedOptionIndex === currentQ.correctIndex;

    this.hasAnsweredCurrentQuestion = true;
    this.isCurrentAnswerCorrect = isCorrect;

    if (isCorrect) {
      this.sessionCorrectCount += 1;
      soundEngine.playSuccess();
      if (this.hud) {
        this.hud.say(`Splendid insight, Tudor! You deduced the exact nuance of "${currentWord.word}".`, 'celebrating');
      }
    } else {
      soundEngine.playTryAgain();
      if (this.hud) {
        this.hud.say(`Consider the context again, Tudor. How is "${currentWord.word}" acting in the field note?`, 'thinking');
      }
    }

    // Persist attempt into ComprehensionStorage
    const attempt: VocabAttempt = {
      vocabId: currentWord.id,
      word: currentWord.word,
      questionId: currentQ.id,
      selectedOptionIndex: this.selectedOptionIndex!,
      correct: isCorrect,
      timestamp: Date.now(),
    };
    ComprehensionStorage.recordVocabAttempt(attempt);

    this.updateView();
  }

  private changeWord(newIndex: number): void {
    this.currentIndex = newIndex;
    this.isDrawerOpen = false;
    this.activeQuestionIndex = 0;
    this.selectedOptionIndex = null;
    this.hasAnsweredCurrentQuestion = false;
    this.isCurrentAnswerCorrect = false;
    this.sessionCorrectCount = 0;
    this.updateView();
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
